const STORAGE_PREFIX = 'lidea.measures.selection.';

/** Only auto-reopen the measure form after a soft reload within this window. */
const FORM_RESTORE_MAX_AGE_MS = 60_000;

export type MeasuresSelectionSession = {
  pointId: number | null;
  formOpen: boolean;
  updatedAt: number;
};

function storageKey(analysisId: number): string {
  return `${STORAGE_PREFIX}${analysisId}`;
}

function readSession(analysisId: number): MeasuresSelectionSession | null {
  try {
    const raw = sessionStorage.getItem(storageKey(analysisId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as MeasuresSelectionSession;
    if (
      typeof parsed !== 'object' ||
      parsed == null ||
      typeof parsed.updatedAt !== 'number'
    ) {
      return null;
    }
    return {
      pointId:
        parsed.pointId == null || !Number.isFinite(Number(parsed.pointId))
          ? null
          : Number(parsed.pointId),
      formOpen: Boolean(parsed.formOpen),
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

function writeSession(
  analysisId: number,
  session: MeasuresSelectionSession
): void {
  try {
    sessionStorage.setItem(storageKey(analysisId), JSON.stringify(session));
  } catch {
    // ignore quota / private mode
  }
}

export function readPersistedSelectedPoint(analysisId: number): number | null {
  return readSession(analysisId)?.pointId ?? null;
}

export function persistSelectedPoint(
  analysisId: number,
  pointId: number | null
): void {
  const prev = readSession(analysisId);
  writeSession(analysisId, {
    pointId,
    formOpen: pointId == null ? false : Boolean(prev?.formOpen),
    updatedAt: Date.now(),
  });
}

export function persistMeasureFormOpen(
  analysisId: number,
  formOpen: boolean
): void {
  const prev = readSession(analysisId);
  writeSession(analysisId, {
    pointId: prev?.pointId ?? null,
    formOpen,
    updatedAt: Date.now(),
  });
}

/** True when the form was open recently (soft-reload recovery). */
export function shouldRestoreMeasureForm(analysisId: number): boolean {
  const session = readSession(analysisId);
  if (!session?.formOpen || session.pointId == null) {
    return false;
  }
  return Date.now() - session.updatedAt < FORM_RESTORE_MAX_AGE_MS;
}
